import { useEffect, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, authFetch } from '@/services/api';
import { shortStationName } from '@/mocks/ownerDashboard';

// Owner dashboard — inverter health (Ф4). Reads /owner/inverter-health
// (asset_inverter_working_estimate_v). working_estimate is an INFERENCE from
// actual-vs-expected PAC, not per-inverter telemetry (SMA stations expose only
// aggregate) — so we label confidence and show honest "no estimate" states at
// night / when there is no Modbus reading.

interface InvAsset {
  assetId: string; assetName: string;
  nameplateKw: number | null; totalInverters: number | null;
  readAtLocal: string | null; actualPacKw: number | null;
  shortwave: number | null; cloudCover: number | null;
  expectedPacKw: number | null; workingRatio: number | null;
  workingEstimate: number | null; confidence: string;
}

type StatusKey = 'ok' | 'minor' | 'major' | 'night' | 'no_telemetry' | 'pending';

function statusOf(a: InvAsset): { key: StatusKey; label: string } {
  if (a.totalInverters == null) return { key: 'pending', label: 'Очікує запуску' };
  if (a.workingEstimate != null) {
    const we = a.workingEstimate, tot = a.totalInverters;
    if (we >= tot) return { key: 'ok', label: `Всі ${tot} інверторів працюють` };
    const off = tot - we;
    if (we >= tot * 0.8) return { key: 'minor', label: `${we}/${tot} працює · ${off} під питанням` };
    return { key: 'major', label: `${we}/${tot} працює · ~${off} offline` };
  }
  if (a.confidence === 'low_light_no_inference') return { key: 'night', label: 'Оцінка вночі недоступна' };
  // no_reading + inverters known → SMA stations without per-inverter Modbus
  return { key: 'no_telemetry', label: 'Без телеметрії інверторів' };
}

export default function InverterHealth() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [assets, setAssets] = useState<InvAsset[] | null>(null);
  const [loading, setLoading] = useState(true);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const rowBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';

  const COLORS: Record<StatusKey, string> = {
    ok: '#22C55E', minor: '#E0A030', major: '#D12229',
    night: '#6B6B70', no_telemetry: '#6B6B70', pending: '#7FB2E6',
  };

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_BASE_URL}/owner/inverter-health`)
      .then((r) => r.json())
      .then((d) => setAssets(d.assets || []))
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, []);

  // portfolio summary (only assets with a real estimate contribute counts)
  const withEst = (assets || []).filter((a) => a.workingEstimate != null && a.totalInverters != null);
  const totWorking = withEst.reduce((s, a) => s + (a.workingEstimate || 0), 0);
  const totInv = withEst.reduce((s, a) => s + (a.totalInverters || 0), 0);
  const anyDay = withEst.length > 0;

  const fmtTime = (iso: string | null) => {
    if (!iso) return '';
    const m = iso.match(/(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  };

  return (
    <div className="rounded-xl p-5 mb-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold" style={{ color: textMain }}>{t('d_inv_title')}</div>
          <div className="text-[10px] mt-0.5" style={{ color: textMuted }}>
            {t('d_inv_sub')}
          </div>
        </div>
        {anyDay && (
          <div className="rounded-lg px-3 py-1.5 text-right" style={{ background: innerBg }}>
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: textMuted }}>{t('d_inv_working_portfolio')}</div>
            <div className="text-base font-bold tabular-nums" style={{ color: totWorking >= totInv ? '#22C55E' : '#E0A030' }}>
              {totWorking}/{totInv}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-xs py-4 text-center" style={{ color: textMuted }}>{t('d_chart_loading')}</div>
      ) : (assets && assets.length > 0) ? (
        <div className="space-y-1.5">
          {assets.map((a) => {
            const st = statusOf(a);
            const color = COLORS[st.key];
            return (
              <div key={a.assetId} className="flex items-center gap-3 py-1.5"
                   style={{ borderBottom: `1px solid ${rowBorder}` }}>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: textMain }}>{shortStationName(a.assetName)}</div>
                  <div className="text-[11px]" style={{ color: textMuted }}>
                    {a.nameplateKw ? `${(a.nameplateKw / 1000).toFixed(1)} ${t('d_u_mw')}` : '—'}
                    {a.totalInverters ? ` · ${a.totalInverters} ${t('d_inv_abbr')}` : ''}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-semibold" style={{ color }}>{st.label}</div>
                  {a.readAtLocal && (st.key === 'ok' || st.key === 'minor' || st.key === 'major') && (
                    <div className="text-[10px]" style={{ color: textMuted }}>{t('d_inv_at')} {fmtTime(a.readAtLocal)}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs py-4 text-center" style={{ color: textMuted }}>{t('d_inv_no_data')}</div>
      )}

      <div className="text-[10px] mt-3 pt-3 leading-relaxed" style={{ color: textMuted, borderTop: `1px solid ${cardBorder}` }}>
        {t('d_inv_footer')}
      </div>
    </div>
  );
}
